import {
  ChevronRightIcon,
  InfoIcon,
  LogOutIcon,
  SettingsIcon,
  UserIcon,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { clearUser } from "../../store/slices/userSlice";
import { toast } from "sonner";
import { authApi } from "../../lib/api";
import { useTranslation } from "react-i18next";

// Menu item data for mapping
const menuItems = [
  {
    icon: <UserIcon className="h-5 w-5" data-testid="user-icon" />,
    title: "myProfile",
    description: "manageProfileDetails",
    showChevron: true,
  },
  // {
  //   icon: <SettingsIcon className="h-5 w-5" data-testid="settings-icon" />,
  //   title: "settings",
  //   description: "notificationsAndLanguage",
  //   showChevron: true,
  // },
  {
    icon: <InfoIcon className="h-5 w-5" data-testid="info-icon" />,
    title: "aboutUs",
    description: "aboutSyngentaAndPolicies",
    showChevron: true,
  },
  {
    icon: <LogOutIcon className="h-5 w-5" data-testid="logout-icon" />,
    title: "logout",
    description: "",
    showChevron: false,
  },
];

interface ProfilePopupProps {
  onClose: () => void;
}

export const ProfilePopup = ({ onClose }: ProfilePopupProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
  const country = countryMatch ? countryMatch[1] : "in";
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const refreshToken = useSelector((state: RootState) => state.user.refreshToken);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleMyProfileClick = () => {
    navigate(`/${country}/edit-profile`);
    onClose();
  };

  const handleSettingsClick = () => {
    navigate(`/${country}/settings`);
    onClose();
  };

  const handleAboutUsClick = () => {
    navigate(`/${country}/about-us`);
    onClose();
  };

  const handleLogout = async () => {
    try {
      const response = await authApi.logout();
      if (response.status === 200 || response.status === 201) {
        // Get current country from URL before clearing storage
        const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
        const country = countryMatch ? countryMatch[1] : "in";
        localStorage.clear();
        localStorage.setItem('lastCountry', country);
        dispatch(clearUser());
        toast.success(response.message);
        navigate(`/${country}/login`);
      } else {
        toast.error(t('errorDuringLogout'));
      }
    } catch (error) {
      toast.error(t('errorDuringLogout'));
    }
  };

  return (
    <Card ref={popupRef} className="h-full w-[85%] sm:w-full sm:h-auto flex flex-col items-start gap-4 p-2.5 bg-white">
      <CardContent className="w-full p-0">
        {menuItems.map((item, index) => (
          <div
            key={index}
            data-testid="menu-item"
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
            onClick={
              item.title === "myProfile"
                ? handleMyProfileClick
                : item.title === "settings"
                ? handleSettingsClick
                : item.title === "aboutUs"
                ? handleAboutUsClick
                : item.title === "logout"
                ? handleLogout
                : undefined
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (item.title === "myProfile") handleMyProfileClick();
                else if (item.title === "settings") handleSettingsClick();
                else if (item.title === "aboutUs") handleAboutUsClick();
                else if (item.title === "logout") handleLogout();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className="flex items-center gap-3">
              {item.icon}
              <div>
                <p className="text-sm font-medium">{t(item.title)}</p>
                {item.description && (
                  <p className="text-xs text-gray-500">{t(item.description)}</p>
                )}
              </div>
            </div>
            {item.showChevron && (
              <ChevronRightIcon
                className="h-5 w-5 text-gray-400"
                data-testid="chevron-right-icon"
              />
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
